import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Plus, Search } from 'lucide-react';
import { apiClient } from '@/services/api';
import { brandKnowledgeApi, PlanCampaignRef } from '@/services/brandKnowledgeApi';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import type { Campaign, Group } from '@/components/campaigns/CampaignSidebar';
import { useDesk } from '@/components/growth-desk/data/DeskData';
import { channelName, DeskCampaign, goalName, monthLabel, money, shortDate } from '@/components/growth-desk/data/status';
import { Chip, ChipTone, cx, DeskButton, DeskLinkButton, DeskStatus, StatusChip } from '@/components/growth-desk/ui';
import { DESK } from '@/components/growth-desk/links';

/** A checkbox drawn by us (the browser's own turns into a dark square under a dark colour scheme). */
const Box: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({ checked, onChange, label }) => (
  <span className="relative inline-grid h-[18px] w-[18px] place-items-center align-middle">
    <input type="checkbox" aria-label={label} checked={checked} onChange={onChange}
      className="peer h-[18px] w-[18px] cursor-pointer appearance-none rounded-[5px] border-[1.5px] border-ink-3 bg-card transition-colors checked:border-ink checked:bg-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" />
    <Check className="pointer-events-none absolute hidden h-3 w-3 text-card peer-checked:block" strokeWidth={3} aria-hidden="true" />
  </span>
);

/**
 * Campaigns (campaigns list board): every campaign, from the plan and from chat, with status
 * in words, filters, folders and bulk actions. Uses the same campaigns / groups API as the old
 * list column; plan campaigns add their plan dates, budget and status; results come from the
 * analytics summary.
 */

type Bucket = 'all' | 'needs' | 'review' | 'production' | 'live' | 'drafts' | 'ended';

interface Row {
  c: Campaign;
  plan: DeskCampaign | null;
  bucket: Exclude<Bucket, 'all'> | 'other';
  status: React.ReactNode;
  channelGoal: string;
  dates: string;
  budget: string;
  results: string;
  posts?: { month: string };        // a month's posts container: opens that month's Posts page
  planMonth?: string;               // a plan campaign of another month than the desk's
}

// Where a plan item stands (server, sweep.status_of) -> the status chip.
const REF_STATUS: Record<string, DeskStatus> = {
  suggested: 'suggested', approved_brief: 'brief_approved', not_started: 'brief_approved', making: 'in_production',
  ready: 'ready', approved: 'approved', scheduled: 'scheduled', published: 'live', failed: 'needs_you',
  missed: 'needs_you', skipped: 'skipped', draft: 'has_campaign', stale: 'changed',
};

type Containers = Awaited<ReturnType<typeof brandKnowledgeApi.postsContainers>>;

const ago = (iso?: string) => {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).getTime()) / 60000);
  if (m < 60) return `${Math.max(1, m)} min`;
  if (m < 1440) return `${Math.round(m / 60)} h`;
  if (m < 2880) return 'Yesterday';
  return `${Math.round(m / 1440)} days`;
};

const STAGE_WORD: Record<string, string> = { brief: 'Draft', strategy: 'Draft · strategy', creative: 'Draft · ads', launch: 'Ready to launch' };

export const CampaignsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const { campaigns: planCampaigns, month } = useDesk();
  const [items, setItems] = useState<Campaign[] | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [containers, setContainers] = useState<Containers>([]);
  const [planRefs, setPlanRefs] = useState<PlanCampaignRef[]>([]);    // plan campaigns of any month
  const [results, setResults] = useState<Record<string, { count: number | null; each: number | null; currency: string | null }>>({});
  const [bucket, setBucket] = useState<Bucket>('all');
  const [origin, setOrigin] = useState<'all' | 'plan' | 'chat'>('all');
  const [folder, setFolder] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newFolder, setNewFolder] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    // The demo campaign exists for every business (idempotent), then the list.
    if (currentOrganization?.id) { try { await apiClient.post('/api/v1/conversations/ensure-demo'); } catch { /* non-critical */ } }
    try {
      const r = await apiClient.get('/api/v1/campaigns', { params: { limit: 100 } });
      const list: Campaign[] = r.data?.conversations || [];
      list.sort((a, b) => (b.is_demo ? 1 : 0) - (a.is_demo ? 1 : 0));
      setItems(list);
    } catch (e: any) { setItems([]); toast.error(e?.message || 'Couldn’t load campaigns'); }
    apiClient.get('/api/v1/groups').then(r => setGroups(r.data || [])).catch(() => setGroups([]));
    brandKnowledgeApi.postsContainers().then(setContainers).catch(() => setContainers([]));
    brandKnowledgeApi.planCampaigns().then(setPlanRefs).catch(() => setPlanRefs([]));
    apiClient.get('/api/v1/analytics/campaigns/summary', { params: { time_range: 'lifetime', limit: 100 } })
      .then(r => setResults(Object.fromEntries((r.data?.campaigns || []).filter((c: any) => c.conversation_id)
        .map((c: any) => [c.conversation_id, { count: c.result_count ?? null, each: c.cost_per_result ?? null, currency: c.currency ?? null }]))))
      .catch(() => setResults({}));
  }, [currentOrganization?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('organizationChanged', load);
    window.addEventListener('campaignScheduled', load);
    return () => { window.removeEventListener('organizationChanged', load); window.removeEventListener('campaignScheduled', load); };
  }, [load]);

  const rows: Row[] = useMemo(() => (items || []).map(c => {
    const plan = planCampaigns.find(p => (p.production?.campaign_id || p.row.campaign_id) === c.id) || null;
    const res = results[c.id];
    const resultText = res?.count ? `${res.count}${res.each ? ` · ${money(res.each, res.currency || 'INR')}` : ''}` : '—';
    const sched = (c.schedule_infos || []).find(s => s.status === 'scheduled' || s.status === 'publishing');
    const stage = (c as any).stage as string | undefined;
    const box = containers.find(x => x.conversation_id === c.id);
    if (box) {
      const st = box.states;
      const n = (k: keyof typeof st) => st[k] || 0;
      const needs = n('missed') + n('failed'), ready = n('ready'), making = n('making'), out = n('scheduled') + n('published');
      const words = [needs && `${needs} need you`, ready && `${ready} to review`, making && `${making} being made`, out && `${out} scheduled or posted`].filter(Boolean);
      return {
        c, plan: null, posts: { month: box.month },
        bucket: needs ? 'needs' : ready ? 'review' : making ? 'production' : out ? 'live' : 'drafts',
        status: <Chip tone={needs ? 'bad' : ready ? 'brand' : making ? 'work' : out ? 'ok' : 'neutral'}>{words[0] || `${box.posts} post${box.posts === 1 ? '' : 's'}`}</Chip>,
        channelGoal: 'Instagram & Facebook · Posts', dates: monthLabel(box.month, false), budget: '', results: words.slice(1).join(' · ') || '—',
      };
    }
    if (plan) {
      const b = plan.row.brief;
      const bucket: Row['bucket'] = ['needs_you', 'stopped'].includes(plan.status) ? 'needs' : plan.status === 'ready' ? 'review'
        : ['in_production', 'waiting', 'paused'].includes(plan.status) ? 'production' : plan.status === 'live' ? 'live' : plan.status === 'ended' ? 'ended' : 'drafts';
      return {
        c, plan, bucket, status: <StatusChip status={plan.status} detail={plan.statusDetail} />,
        channelGoal: [channelName(b.channel), goalName(b.objective)].filter(Boolean).join(' · '),
        dates: `${shortDate(b.start_date)} – ${shortDate(b.end_date)}`, budget: money(b.budget, 'INR'), results: resultText,
      };
    }
    const ref = planRefs.find(x => x.campaign_id === c.id);
    if (ref) {                 // a plan campaign of another month than the desk's
      const st = REF_STATUS[ref.state] || 'planned';
      const ended = st === 'live' && ref.end_date && ref.end_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
      const ds: DeskStatus = ended ? 'ended' : st;
      const bucket: Row['bucket'] = ['needs_you', 'stopped'].includes(ds) ? 'needs' : ds === 'ready' ? 'review'
        : ds === 'in_production' ? 'production' : ds === 'live' || ds === 'scheduled' ? 'live' : ds === 'ended' ? 'ended' : 'drafts';
      return {
        c, plan: null, planMonth: ref.month, bucket, status: <StatusChip status={ds} />,
        channelGoal: [channelName(ref.channel), goalName(ref.objective)].filter(Boolean).join(' · '),
        dates: ref.start_date && ref.end_date ? `${shortDate(ref.start_date)} – ${shortDate(ref.end_date)}` : '',
        budget: ref.budget ? money(ref.budget, 'INR') : '', results: resultText,
      };
    }
    let status: React.ReactNode; let bucket: Row['bucket'];
    if (c.status === 'archived') { status = <Chip tone="muted">Archived</Chip>; bucket = 'other'; }
    else if (c.status === 'failed') { status = <StatusChip status="stopped" />; bucket = 'needs'; }
    else if (sched) { status = <StatusChip status="scheduled" detail={shortDate(sched.scheduled_at)} />; bucket = 'production'; }
    else if (stage === 'live' || c.status === 'published') { status = <StatusChip status="live" />; bucket = 'live'; }
    else if (stage === 'launch') { status = <Chip tone="brand">Ready to launch</Chip>; bucket = 'review'; }
    else { status = <Chip tone={'neutral' as ChipTone}>{STAGE_WORD[stage || 'brief'] || 'Draft'}</Chip>; bucket = 'drafts'; }
    return { c, plan: null, bucket, status, channelGoal: '', dates: '', budget: '', results: resultText };
  }), [items, planCampaigns, results, containers, planRefs]);

  const inFolder = (r: Row) => folder === 'all' ? r.c.status !== 'archived' : folder === 'archived' ? r.c.status === 'archived' : r.c.group_id === folder;
  const visible = rows.filter(r => inFolder(r)
    && (bucket === 'all' || r.bucket === bucket)
    && (origin === 'all' || (origin === 'plan') === !!(r.plan || r.posts || r.planMonth))
    && (!query.trim() || r.c.title.toLowerCase().includes(query.trim().toLowerCase())));
  const count = (b: Bucket) => rows.filter(r => inFolder(r) && (b === 'all' || r.bucket === b)).length;
  const BUCKETS: { id: Bucket; label: string }[] = [
    { id: 'all', label: 'All' }, { id: 'needs', label: 'Needs you' }, { id: 'review', label: 'Ready for review' },
    { id: 'production', label: 'In production' }, { id: 'live', label: 'Live' }, { id: 'drafts', label: 'Drafts' }, { id: 'ended', label: 'Ended' },
  ];

  const newCampaign = async () => {
    setCreating(true);
    try {
      const r = await apiClient.post('/api/v1/campaigns', { title: 'New Campaign' });
      navigate(`/campaigns/${r.data.id}`);
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data?.error || err.response?.data;
      if (status === 402) toast.warning(data?.limit != null ? `Campaign limit reached (${data.current}/${data.limit}). Upgrade your plan to create more campaigns.` : data?.message || 'Campaign creation requires a plan upgrade.', 'Limit reached');
      else navigate('/campaigns/new');
    } finally { setCreating(false); }
  };
  const moveTo = async (groupId: string | null) => {
    const ids = [...selected];
    await Promise.all(ids.map(id => apiClient.patch(`/api/v1/campaigns/${id}`, { group_id: groupId }).catch(() => null)));
    toast.success(groupId ? 'Moved to the folder' : 'Removed from the folder'); setSelected(new Set()); load();
  };
  const remove = async () => {
    const ids = [...selected];
    const ok = await confirm(`Delete ${ids.length} campaign${ids.length === 1 ? '' : 's'}? Live ads keep running on the platforms until you stop them there.`, { variant: 'danger', confirmLabel: 'Delete', cancelLabel: 'Keep' });
    if (!ok) return;
    await Promise.all(ids.map(id => apiClient.delete(`/api/v1/campaigns/${id}`).catch(() => null)));
    toast.success('Deleted'); setSelected(new Set()); load();
  };
  const duplicate = async () => {
    const [id] = [...selected];
    try { const r = await apiClient.post(`/api/v1/conversations/${id}/clone`, {}); toast.success('Duplicated'); setSelected(new Set()); if (r.data?.id) navigate(`/campaigns/${r.data.id}`); else load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Couldn’t duplicate'); }
  };
  const createFolder = async () => {
    const name = (newFolder || '').trim(); if (!name) return;
    const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    try { await apiClient.post('/api/v1/groups', { name, color: colors[groups.length % colors.length] }); setNewFolder(null); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Couldn’t create the folder'); }
  };
  const deleteFolder = async (g: Group) => {
    const ok = await confirm(`Delete the folder “${g.name}”? Its campaigns stay.`, { variant: 'danger', confirmLabel: 'Delete folder', cancelLabel: 'Keep' });
    if (!ok) return;
    try { await apiClient.delete(`/api/v1/groups/${g.id}`); if (folder === g.id) setFolder('all'); load(); } catch (e: any) { toast.error(e?.response?.data?.detail || 'Couldn’t delete the folder'); }
  };
  const toggle = (id: string) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = visible.length > 0 && visible.every(r => selected.has(r.c.id));
  const th = 'pb-3 pr-3 text-left text-label uppercase text-ink-3 whitespace-nowrap';
  const folderBtn = (id: string, label: string, n: number, extra?: React.ReactNode) => (
    <div key={id} className="group flex items-center">
      <button type="button" onClick={() => setFolder(id)} aria-current={folder === id ? 'true' : undefined}
        className={cx('flex min-h-[40px] flex-1 items-center justify-between rounded-[10px] px-3 text-left text-sm font-semibold', folder === id ? 'bg-card text-ink' : 'text-ink-2 hover:bg-card')}>
        <span className="truncate">{label}</span><span className="font-money text-xs text-ink-3">{n}</span>
      </button>{extra}
    </div>
  );

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-5 pb-12 pt-4 md:px-12">
      <header className="flex flex-col gap-3.5">
        <div className="text-label uppercase text-brand-ink">From your plan and from chat</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="m-0 font-display text-[34px] font-extrabold leading-none tracking-[-0.035em] text-ink md:text-display-l">Campaigns</h1>
          <div className="flex flex-wrap gap-2">
            <DeskLinkButton to={DESK.newPost()} icon={<Plus className="h-4 w-4" />}>New post</DeskLinkButton>
            <DeskButton variant="primary" loading={creating} onClick={newCampaign} icon={<Plus className="h-4 w-4" />}>New campaign</DeskButton>
          </div>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {BUCKETS.map(b => (
          <button key={b.id} type="button" onClick={() => setBucket(b.id)} aria-pressed={bucket === b.id}
            className={cx('min-h-[40px] rounded-full px-4 text-sm font-semibold transition-colors', bucket === b.id ? 'bg-navy text-on-navy' : 'bg-card text-ink shadow-ring-line hover:shadow-ring-ink')}>
            {b.label} · {count(b.id)}
          </button>
        ))}
        <span className="mx-1 h-6 w-px bg-line-2" aria-hidden="true" />
        <select value={origin} onChange={e => setOrigin(e.target.value as any)} aria-label="Plan or chat"
          className="min-h-[40px] rounded-full border-0 bg-card px-4 text-sm font-semibold text-ink shadow-ring-line">
          <option value="all">Plan or chat</option><option value="plan">From a monthly plan</option><option value="chat">Made in chat</option>
        </select>
        <label className="relative ml-auto">
          <span className="sr-only">Search campaigns</span>
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" aria-hidden="true" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search campaigns"
            className="min-h-[40px] w-[240px] rounded-full border-0 bg-card pl-10 pr-4 text-sm shadow-ring-line focus:outline-none focus:shadow-ring-ink" />
        </label>
      </div>

      <div className="flex flex-wrap items-start gap-6">
        <nav aria-label="Folders" className="flex w-full flex-col gap-1 md:w-[180px]">
          <div className="px-3 pb-1 text-label uppercase text-ink-3">Folders</div>
          {folderBtn('all', 'All', rows.filter(r => r.c.status !== 'archived').length)}
          {groups.map(g => folderBtn(g.id, g.name, rows.filter(r => r.c.group_id === g.id).length,
            <button type="button" onClick={() => deleteFolder(g)} aria-label={`Delete folder ${g.name}`} className="ml-1 hidden h-8 w-8 place-items-center rounded-lg text-ink-3 hover:text-bad group-hover:grid">×</button>))}
          {folderBtn('archived', 'Archived', rows.filter(r => r.c.status === 'archived').length)}
          {newFolder === null
            ? <button type="button" onClick={() => setNewFolder('')} className="min-h-[40px] px-3 text-left text-sm font-semibold text-brand-ink">+ New folder</button>
            : <form onSubmit={e => { e.preventDefault(); createFolder(); }} className="flex gap-1.5 px-1">
                <input autoFocus value={newFolder} onChange={e => setNewFolder(e.target.value)} aria-label="Folder name" placeholder="Folder name"
                  className="min-h-[36px] min-w-0 flex-1 rounded-lg border-0 bg-card px-2.5 text-sm shadow-ring-line focus:outline-none focus:shadow-ring-ink" />
                <DeskButton type="submit" size="xs" variant="navy">Add</DeskButton>
              </form>}
        </nav>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {selected.size > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-card bg-navy px-[18px] py-2.5 text-on-navy">
              <span className="font-display text-sm font-bold">{selected.size} selected</span>
              <div className="flex flex-wrap gap-2">
                <select aria-label="Move to folder" defaultValue="" onChange={e => { if (e.target.value) moveTo(e.target.value === '__none' ? null : e.target.value); e.target.value = ''; }}
                  className="min-h-[40px] rounded-btn border-0 bg-navy-3 px-3 font-display text-sm font-bold text-on-navy">
                  <option value="" disabled>Move to folder</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  <option value="__none">No folder</option>
                </select>
                {selected.size === 1 && <DeskButton size="sm" variant="quiet" onClick={duplicate}>Duplicate</DeskButton>}
                <DeskButton size="sm" variant="quiet" onClick={remove}>Delete</DeskButton>
              </div>
            </div>
          )}
          <div className="overflow-x-auto rounded-panel bg-card px-6 pb-2 pt-5">
            <table className="w-full min-w-[860px] table-fixed border-collapse">
              <colgroup>
                <col className="w-9" /><col className="w-[27%]" /><col className="w-[13%]" /><col className="w-[14%]" />
                <col className="w-[9%]" /><col className="w-[17%]" /><col className="w-[12%]" /><col className="w-[8%]" />
              </colgroup>
              <thead>
                <tr className="border-b-[1.5px] border-b-ink">
                  <th className="pb-3 text-left"><Box label="Select all" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(visible.map(r => r.c.id)))} /></th>
                  <th className={th}>Campaign</th><th className={th}>Channel</th><th className={th}>Dates</th><th className={`${th} text-right`}>Budget</th>
                  <th className={th}>Status</th><th className={th}>Results</th><th className={`${th} pr-1 text-right`}>Active</th>
                </tr>
              </thead>
              <tbody>
                {items === null && <tr><td colSpan={8} className="py-8 text-center text-small text-ink-3">Loading…</td></tr>}
                {items !== null && visible.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-small text-ink-3">
                    No campaigns match. {(bucket !== 'all' || origin !== 'all' || query) && <button type="button" className="font-semibold text-brand-ink" onClick={() => { setBucket('all'); setOrigin('all'); setQuery(''); }}>Clear filters</button>}
                  </td></tr>
                )}
                {visible.map(r => (
                  <tr key={r.c.id} className={cx('border-b border-line align-middle transition-colors last:border-b-0 hover:bg-ground/60', selected.has(r.c.id) && 'bg-brand-soft/40')}>
                    <td className="py-3.5"><Box label={`Select ${r.c.title}`} checked={selected.has(r.c.id)} onChange={() => toggle(r.c.id)} /></td>
                    <td className="py-3.5 pr-4">
                      <Link to={r.posts ? `/month/${r.posts.month}/posts` : `/campaigns/${r.c.id}`} className="flex min-w-0 flex-col gap-0.5 no-underline">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="line-clamp-2 font-display text-[15px] font-bold leading-snug text-ink hover:underline" title={r.c.title}>{r.c.title}</span>
                          {r.c.is_demo && <Chip tone="brand" className="shrink-0">Demo</Chip>}
                        </span>
                        <span className="truncate text-xs text-ink-3">{r.posts ? `All ${monthLabel(r.posts.month, false)} posts, planned and your own` : r.plan ? `From the ${monthLabel(month, false)} plan` : r.planMonth ? `From the ${monthLabel(r.planMonth, false)} plan` : 'Made in chat'}</span>
                      </Link>
                    </td>
                    <td className="py-3.5 pr-3 text-sm leading-snug">
                      {r.channelGoal ? (() => {
                        const [ch, ...goal] = r.channelGoal.split(' · ');
                        return <span className="flex flex-col"><span>{ch}</span>{goal.length > 0 && <span className="text-xs text-ink-3">{goal.join(' · ')}</span>}</span>;
                      })() : <span className="text-ink-3">Not set</span>}
                    </td>
                    <td className="whitespace-nowrap py-3.5 pr-3 text-sm leading-snug">{r.dates || <span className="text-ink-3">Not set</span>}</td>
                    <td className="whitespace-nowrap py-3.5 pr-4 text-right font-money text-sm font-semibold">{r.budget || <span className="font-sans font-normal text-ink-3">—</span>}</td>
                    <td className="py-3.5 pr-3 [&>span]:h-auto [&>span]:min-h-6 [&>span]:max-w-full [&>span]:whitespace-normal [&>span]:py-1 [&>span]:leading-tight">{r.status}</td>
                    <td className="py-3.5 pr-3 text-small leading-snug text-ink-2">{r.results && r.results !== '—'
                      ? <span className="flex flex-col">{r.results.split(' · ').map((x, i) => <span key={i}>{x}</span>)}</span>
                      : <span className="text-ink-3">—</span>}</td>
                    <td className="whitespace-nowrap py-3.5 pr-1 text-right text-small text-ink-3">{ago(r.c.last_activity_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="m-0 text-small text-ink-3">Plan campaigns show their plan’s dates, budget and status; results come from your ad accounts.</p>
        </div>
      </div>
    </div>
  );
};

export default CampaignsListPage;
